// Competitor Watcher + Yield Management view
import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Eye, TrendingUp, TrendingDown, Minus, Plus, Trash2, RefreshCw,
  Zap, Target, ChevronRight, Loader2, Check, Star
} from "lucide-react";
import { CHANNELS } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CompetitorYield() {
  const [competitors, setCompetitors] = useState([]);
  const [market, setMarket] = useState({ days: [] });
  const [recs, setRecs] = useState(null);
  const [config, setConfig] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [newC, setNewC] = useState({ name: "", location: "", channel: "booking_com", bedrooms: 2, sleeps: 4, base_price_eur: 180 });
  const [msg, setMsg] = useState("");

  const loadAll = async () => {
    const [c, m, r, cfg] = await Promise.all([
      axios.get(`${API}/competitor/competitors`),
      axios.get(`${API}/competitor/market`),
      axios.get(`${API}/competitor/yield/recommendations`),
      axios.get(`${API}/competitor/yield/config`),
    ]);
    setCompetitors(c.data); setMarket(m.data); setRecs(r.data); setConfig(cfg.data);
  };
  useEffect(() => { loadAll(); }, []);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/competitor/scan`);
      setMsg(`✓ ${r.data.scanned} Konkurrenz-Objekte neu geprüft`);
      await loadAll();
    } finally { setScanning(false); setTimeout(() => setMsg(""), 2500); }
  };

  const addCompetitor = async () => {
    if (!newC.name || !newC.location) return;
    await axios.post(`${API}/competitor/competitors`, newC);
    setNewC({ name: "", location: "", channel: "booking_com", bedrooms: 2, sleeps: 4, base_price_eur: 180 });
    await loadAll();
  };

  const deleteCompetitor = async (id) => {
    await axios.delete(`${API}/competitor/competitors/${id}`);
    await loadAll();
  };

  const saveConfig = async (patch) => {
    const next = { ...config, ...patch };
    setConfig(next);
    await axios.put(`${API}/competitor/yield/config`, next);
    const r = await axios.get(`${API}/competitor/yield/recommendations`);
    setRecs(r.data);
  };

  const applyAll = async () => {
    if (!recs) return;
    setApplying(true);
    const updates = [];
    for (const p of recs.properties) {
      for (const d of p.days) {
        if (Math.abs(d.delta_pct) >= 2) {
          updates.push({ property_id: p.property_id, date: d.date, price: d.recommended });
        }
      }
    }
    try {
      const r = await axios.post(`${API}/competitor/yield/apply`, { updates });
      setMsg(`✓ ${r.data.applied} Preis-Updates auf alle Kanäle gepusht`);
    } finally { setApplying(false); setTimeout(() => setMsg(""), 3500); }
  };

  if (!config) return <div className="text-eb-muted">Lade…</div>;

  const maxMarket = Math.max(1, ...market.days.map((d) => d.max_eur));
  const signalColor = (s) => ({ boost: "#10B981", discount: "#38BDF8", hold: "#9CA3AF" }[s]);

  return (
    <div className="space-y-5" data-testid="competitor-view">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-2xl flex items-center gap-2">
            <Eye size={22} className="text-eb-primary" /> Wettbewerb & Yield-Management
          </h2>
          <p className="text-eb-muted text-sm">Konkurrenz-Preise beobachten · Marktdurchschnitt · automatische Preissteuerung</p>
        </div>
        <div className="flex gap-2 items-center">
          {msg && <span className="text-emerald-400 text-xs">{msg}</span>}
          <button className="btn" onClick={scan} disabled={scanning} data-testid="scan-button">
            {scanning ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Markt scannen
          </button>
          <button className="btn btn-primary" onClick={applyAll} disabled={applying} data-testid="apply-yield-button">
            {applying ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Alle Empfehlungen anwenden
          </button>
        </div>
      </div>

      {/* YIELD CONFIG */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Yield-Steuerung</div>
            <div className="text-xs text-eb-muted">Modus · Aggressivität · Regeln für die automatische Preisgestaltung</div>
          </div>
          <div className="flex gap-1 eb-surface rounded-full p-1">
            {["off", "advisory", "auto"].map((m) => (
              <button key={m} onClick={() => saveConfig({ mode: m })}
                data-testid={`yield-mode-${m}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition ${config.mode === m ? "bg-eb-primary text-[#0B0F17]" : "text-eb-muted"}`}>
                {m === "off" ? "Aus" : m === "advisory" ? "Empfehlung" : "Vollautomatik"}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <div className="overline mb-1">Aggressivität {Math.round(config.aggressiveness * 100)} %</div>
            <input type="range" min="0" max="1" step="0.05" value={config.aggressiveness}
              onChange={(e) => saveConfig({ aggressiveness: parseFloat(e.target.value) })}
              data-testid="yield-aggressiveness"
              style={{ width: "100%", padding: 0 }} />
          </div>
          <div>
            <div className="overline mb-1">Weekend-Boost</div>
            <input type="number" value={config.weekend_boost_pct}
              onChange={(e) => saveConfig({ weekend_boost_pct: parseFloat(e.target.value || 0) })}
              data-testid="yield-weekend" />
          </div>
          <div>
            <div className="overline mb-1">Event-Boost</div>
            <input type="number" value={config.event_boost_pct}
              onChange={(e) => saveConfig({ event_boost_pct: parseFloat(e.target.value || 0) })} />
          </div>
          <div>
            <div className="overline mb-1">Max. Aufschlag %</div>
            <input type="number" value={config.max_markup_pct}
              onChange={(e) => saveConfig({ max_markup_pct: parseFloat(e.target.value || 0) })} />
          </div>
          <div>
            <div className="overline mb-1">Max. Rabatt %</div>
            <input type="number" value={config.min_markup_pct}
              onChange={(e) => saveConfig({ min_markup_pct: parseFloat(e.target.value || 0) })} />
          </div>
        </div>
      </div>

      {/* MARKET CHART */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div className="overline">Marktpreis-Entwicklung (30 Tage)</div>
          <div className="text-xs text-eb-muted">{market.competitors} Konkurrenz-Objekte beobachtet</div>
        </div>
        <svg viewBox="0 0 600 180" className="w-full h-44" preserveAspectRatio="none" data-testid="market-chart">
          <defs>
            <linearGradient id="market-area" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
            </linearGradient>
          </defs>
          {market.days.length > 1 && (() => {
            const pts = (key) => market.days.map((d, i) => [
              (i / (market.days.length - 1)) * 600,
              170 - (d[key] / maxMarket) * 150,
            ]);
            const toPath = (p) => p.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ");
            const avg = pts("avg_eur"), lo = pts("min_eur"), hi = pts("max_eur");
            const band = toPath(hi) + " " + hi.slice().reverse().map(([x, y]) => `L${x},${y}`).join(" ") + " Z";
            return (<>
              <path d={toPath(hi) + " L600,180 L0,180 Z"} fill="url(#market-area)" />
              <path d={toPath(lo)} stroke="#38BDF8" strokeWidth="1.2" strokeDasharray="4,4" fill="none" opacity="0.6" />
              <path d={toPath(hi)} stroke="#F43F5E" strokeWidth="1.2" strokeDasharray="4,4" fill="none" opacity="0.6" />
              <path d={toPath(avg)} stroke="#D4AF37" strokeWidth="2.5" fill="none" />
            </>);
          })()}
        </svg>
        <div className="flex gap-4 text-[11px] mt-2">
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-eb-primary inline-block" /> Durchschnitt</span>
          <span className="flex items-center gap-1 text-eb-muted"><span className="w-3 h-0.5 bg-sky-400 inline-block opacity-60" /> Min</span>
          <span className="flex items-center gap-1 text-eb-muted"><span className="w-3 h-0.5 bg-rose-400 inline-block opacity-60" /> Max</span>
        </div>
      </div>

      {/* RECOMMENDATIONS PER PROPERTY */}
      {recs && recs.properties.map((p) => (
        <div key={p.property_id} className="card" data-testid={`recs-${p.easybooking_id}`}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-heading font-bold text-base">{p.name}</div>
              <div className="text-xs text-eb-muted">
                Basis-Preis: € {p.base_price} · Belegung: <span className={
                  p.occupancy_signal === "high" ? "text-emerald-400" :
                  p.occupancy_signal === "low" ? "text-rose-400" : "text-amber-400"
                }>{p.occupancy_signal}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-sm">Ø Delta: <span className={p.summary.avg_delta_pct > 0 ? "text-emerald-400" : "text-rose-400"}>
                {p.summary.avg_delta_pct > 0 ? "+" : ""}{p.summary.avg_delta_pct}%
              </span></div>
              <div className="font-mono text-[11px] text-eb-muted">€ {p.summary.min_recommend} – € {p.summary.max_recommend}</div>
            </div>
          </div>
          <div className="grid grid-cols-10 md:grid-cols-15 lg:grid-cols-30 gap-1">
            {p.days.map((d) => {
              const col = signalColor(d.signal);
              return (
                <div key={d.date} className="eb-surface rounded border border-white/5 p-1 text-center"
                  title={`${d.date} · € ${d.recommended} (${d.delta_pct > 0 ? "+" : ""}${d.delta_pct}%)`}
                  style={{ borderTop: `2px solid ${col}` }}
                  data-testid={`rec-day-${p.easybooking_id}-${d.date}`}>
                  <div className="text-[9px] text-eb-muted font-mono">{d.date.slice(5)}</div>
                  <div className="text-[11px] font-bold" style={{ color: col }}>€ {Math.round(d.recommended)}</div>
                  <div className="text-[9px]" style={{ color: col }}>{d.delta_pct > 0 ? "+" : ""}{d.delta_pct}%</div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* COMPETITORS LIST */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div className="card !p-0 overflow-hidden">
          <div className="overline px-4 py-3 border-b border-white/5">Beobachtete Konkurrenz-Objekte ({competitors.length})</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="eb-surface">
                <th className="text-left px-3 py-2 overline">Objekt</th>
                <th className="text-left px-3 py-2 overline">Ort</th>
                <th className="text-left px-3 py-2 overline">Kanal</th>
                <th className="text-left px-3 py-2 overline">Sterne</th>
                <th className="text-right px-3 py-2 overline">Basis €</th>
                <th className="text-right px-3 py-2 overline">Ø 30d €</th>
                <th className="text-right px-3 py-2 overline">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {competitors.map((c) => {
                const chCol = CHANNELS[c.channel];
                return (
                  <tr key={c.id} className="border-t border-white/5" data-testid={`competitor-row-${c.id}`}>
                    <td className="px-3 py-2.5 font-semibold">{c.name}</td>
                    <td className="px-3 py-2.5 text-eb-muted text-xs">{c.location}</td>
                    <td className="px-3 py-2.5">
                      <span className="channel-pill" style={{ background: chCol?.bg, color: chCol?.fg }}>
                        {chCol?.label || c.channel}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-amber-400 text-xs">
                      {c.stars ? <>★ {c.stars}</> : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">€ {c.base_price_eur}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-eb-primary">
                      {c.last_avg_price ? `€ ${c.last_avg_price}` : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button onClick={() => deleteCompetitor(c.id)} className="text-eb-muted hover:text-rose-400"
                        data-testid={`competitor-delete-${c.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="card">
          <div className="overline mb-3">Konkurrenz hinzufügen</div>
          <div className="space-y-2">
            <input placeholder="Name" value={newC.name}
              onChange={(e) => setNewC({ ...newC, name: e.target.value })}
              data-testid="competitor-name" />
            <input placeholder="Ort" value={newC.location}
              onChange={(e) => setNewC({ ...newC, location: e.target.value })}
              data-testid="competitor-location" />
            <select value={newC.channel} onChange={(e) => setNewC({ ...newC, channel: e.target.value })}
              data-testid="competitor-channel">
              {Object.entries(CHANNELS).filter(([k]) => k !== "direct").map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <div className="grid grid-cols-3 gap-2">
              <input type="number" value={newC.bedrooms} placeholder="SZ"
                onChange={(e) => setNewC({ ...newC, bedrooms: parseInt(e.target.value || 1) })} />
              <input type="number" value={newC.sleeps} placeholder="Pers"
                onChange={(e) => setNewC({ ...newC, sleeps: parseInt(e.target.value || 2) })} />
              <input type="number" value={newC.base_price_eur} placeholder="€/Nacht"
                onChange={(e) => setNewC({ ...newC, base_price_eur: parseFloat(e.target.value || 100) })} />
            </div>
            <button className="btn btn-primary w-full justify-center" onClick={addCompetitor}
              data-testid="competitor-add">
              <Plus size={13} /> Hinzufügen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
